import type { MouseEvent, ReactNode } from "react";
import { Icon, type IconName } from "../components/Icon";

export interface SidebarItem {
  /** Unique within the whole sidebar, not just its group. */
  key: string;
  label: string;
  icon: IconName;
  /** The section's own URL, so the item is a real link: it shows its
   * destination on hover and opens in a new tab on Cmd/Ctrl/middle-click. */
  href?: string;
  active?: boolean;
  /** True for a nav slot that exists but has no screen behind it yet
   * (the Flows-list and Developer tabs land in later slices). Rendered
   * visibly, never hidden, so the eventual feature isn't a surprise —
   * but never clickable, since there is nothing real to go to. */
  disabled?: boolean;
  onSelect?: () => void;
}

export interface SidebarGroup {
  /** Omitted for the single top-level "Overview" entry, which reads
   * better with no group header above it. */
  label?: string;
  items: SidebarItem[];
}

interface SidebarProps {
  groups: SidebarGroup[];
  /** Desktop icon-only rail. Ignored below the mobile breakpoint, where the
   * sidebar is an off-canvas drawer instead (see `mobileOpen`). */
  collapsed: boolean;
  /** Mobile drawer state. Toggled from the topbar's menu button. */
  mobileOpen: boolean;
  onCloseMobile: () => void;
  brand: ReactNode;
  /** Case-insensitive label filter from the topbar search box. Narrowed to
   * "which nav items currently match", not a cross-tenant data search —
   * there is no search backend. */
  filter: string;
}

/** True for an unmodified primary click — the only kind handled in-page;
 * any other click is left to the browser (new tab, new window, …). */
export function isPlainLeftClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

export function Sidebar({ groups, collapsed, mobileOpen, onCloseMobile, brand, filter }: SidebarProps) {
  const needle = filter.trim().toLowerCase();
  const visibleGroups = needle
    ? groups
        .map((group) => ({ ...group, items: group.items.filter((item) => item.label.toLowerCase().includes(needle)) }))
        .filter((group) => group.items.length > 0)
    : groups;

  return (
    <>
      <aside
        className={`sidebar${collapsed ? " sidebar--collapsed" : ""}${mobileOpen ? " sidebar--mobile-open" : ""}`}
        aria-label="Primary"
      >
        <div className="sidebar__brand">{brand}</div>
        <nav aria-label="Dashboard sections">
          {visibleGroups.map((group, index) => (
            <div className="sidebar__group" key={group.label ?? `group-${index}`}>
              <p className="sidebar__group-label">
                <span className="sidebar__group-dots" aria-hidden="true">
                  ···
                </span>
                <span className="sidebar__group-text">{group.label ?? "Menu"}</span>
              </p>
              <ul className="sidebar__list">
                {group.items.map((item) => (
                  <li key={item.key}>
                    {item.href && !item.disabled ? (
                      <a
                        href={item.href}
                        className={`sidebar__item${item.active ? " sidebar__item--active" : ""}`}
                        aria-current={item.active ? "page" : undefined}
                        title={collapsed ? item.label : undefined}
                        onClick={(event) => {
                          if (!isPlainLeftClick(event)) return;
                          event.preventDefault();
                          item.onSelect?.();
                          onCloseMobile();
                        }}
                      >
                        {itemContent(item)}
                      </a>
                    ) : (
                      <button
                        type="button"
                        className={`sidebar__item${item.active ? " sidebar__item--active" : ""}${
                          item.disabled ? " sidebar__item--disabled" : ""
                        }`}
                        aria-current={item.active ? "page" : undefined}
                        aria-disabled={item.disabled || undefined}
                        disabled={item.disabled}
                        onClick={
                          item.disabled
                            ? undefined
                            : () => {
                                item.onSelect?.();
                                onCloseMobile();
                              }
                        }
                        title={
                          collapsed
                            ? item.disabled
                              ? `${item.label} — coming soon`
                              : item.label
                            : item.disabled
                              ? "Coming soon"
                              : undefined
                        }
                      >
                        {itemContent(item)}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {needle && visibleGroups.length === 0 ? (
            <p className="sidebar__empty">No sections match &ldquo;{filter}&rdquo;.</p>
          ) : null}
        </nav>
      </aside>
      {mobileOpen ? <div className="sidebar__backdrop" onClick={onCloseMobile} aria-hidden="true" /> : null}
    </>
  );
}

function itemContent(item: SidebarItem) {
  return (
    <>
      <span className="sidebar__item-icon">
        <Icon name={item.icon} />
      </span>
      <span className="sidebar__item-label">
        {item.label}
        {item.disabled ? <span className="sidebar__item-badge">Soon</span> : null}
      </span>
    </>
  );
}
